import { Api } from '@/Api';
import { ref } from 'vue';
import { useCodeClient } from 'vue3-google-signin';

import { DEBUG_NAMESPACE, useDebug } from './useDebug';
import { useToast } from './useToast';

const APPLE_ID_URL = (redirectTo: string) => `/auth/apple?web=true&return_to=${redirectTo}`;
const SLACK_ID_URL = (redirectTo: string) => `/auth/slack?web=true&return_to=${redirectTo}`;
const SSO_URL = () => '/sso/new?utm_campaign=signup_modal&utm_source=viewer';

export enum AuthActions {
  GOOGLE = 'google',
  APPLE = 'apple',
  SSO = 'sso',
  SLACK = 'slack',
  EMAIL = 'email', // custom action needed for mobile signup modal
}

export const useAuth = (redirectTo: string = window.location.href) => {
  const { error } = useToast();
  const { debug } = useDebug(DEBUG_NAMESPACE.SOCIAL_LOGIN);
  const inProgress = ref(false);

  redirectTo = `${redirectTo}?utm_source=viewer&utm_medium=social_signIn&utm_version=copernicus`;

  const handleGoogleSuccess = async (response: any) => {
    await Api.user
      .socialLogin('google_oauth2', response)
      // @todo for now we reloading application after google auth to preserve constant behavior across apple/slack/google
      .then(reqResponse => {
        if (reqResponse && reqResponse.redirectTo) {
          window.location.assign(reqResponse.redirectTo);
          return;
        }

        if (redirectTo) {
          window.location.assign(redirectTo);
          return;
        }

        window.location.assign('/home');
      })
      .catch((err: Error) => {
        error('There is a problem with logging in using Google provider');
        debug('Error response from server', err);
      });
  };

  const handleGoogleError = (err: any) => {
    error('Google login provider error');
    debug('Error response from google', err);
  };

  /**
   * Callback needed to login - first request to google api then redirect to dropper
   */
  const { login: googleAuth } = useCodeClient({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
  });

  const googleLogin = () => {
    inProgress.value = true;
    googleAuth();
  };

  /**
   * Use direct link to login
   */
  const appleLogin = () => {
    window.location.assign(APPLE_ID_URL(redirectTo));
  };

  /**
   * Use direct link to login
   */
  const slackLogin = () => {
    window.location.assign(SLACK_ID_URL(redirectTo));
  };

  const ssoLogin = () => {
    window.location.assign(SSO_URL());
  };

  const onSocialAction = (action: string) => {
    switch (action) {
      case AuthActions.GOOGLE:
        googleLogin();
        break;
      case AuthActions.APPLE:
        appleLogin();
        break;
      case AuthActions.SLACK:
        slackLogin();
        break;
      case AuthActions.SSO:
        ssoLogin();
        break;
    }
  };

  return { googleLogin, appleLogin, ssoLogin, slackLogin, onSocialAction, inProgress };
};
